/**
 * useDataBrowser 훅
 *
 * 데이터 브라우저의 단일 진실원.
 * - 모듈/테이블 목록은 정적 카탈로그에서 (API 미제공)
 * - rows: [module, table, page, debouncedSearch] 변경 시 fetchTableRows
 * - schema 호출 안 함(rows 응답의 columns 사용)
 * - related: 선택 행 + 현재 테이블 fk로 fetchRelatedRows
 *
 * DataBrowserContainer에서 사용
 */

import { useEffect, useState, useCallback } from 'react';
import * as endpoints from '../api/endpoints';
import {
  DATA_MODULES,
  getTables,
  getTableDef,
  firstTable,
} from '../config/dataCatalog';

export interface UseDataBrowserReturn {
  module: string;
  moduleOptions: string[];
  setModule: (m: string) => void;
  table: string;
  tableOptions: { label: string; value: string }[];
  setTable: (t: string) => void;
  search: string;
  setSearch: (s: string) => void;
  page: number;
  setPage: (p: number) => void;
  columns: string[];
  rows: (string | number)[][];
  total: number;
  loading: boolean;
  error: string | null;
  retry: () => void;
  selectedIndex: number;
  setSelectedIndex: (i: number) => void;
  /** 행 상세 드로어 열림 여부. */
  detailOpen: boolean;
  /** 행 클릭: 선택 + 드로어 열기 + 이전 연관 행 초기화. */
  openDetail: (i: number) => void;
  /** 드로어 닫기 + 연관 행 초기화. */
  closeDetail: () => void;
  fk?: { table: string; label: string };
  related: { title: string; columns: string[]; rows: (string | number)[][] } | null;
  relatedLoading: boolean;
  relatedError: string | null;
  openRelated: () => void;
  clearRelated: () => void;
}

const LIMIT = 50;

export function useDataBrowser(token: string | null): UseDataBrowserReturn {
  const [module, setModuleState] = useState<string>(DATA_MODULES[0] ?? '');
  const [table, setTableState] = useState<string>(firstTable(DATA_MODULES[0] ?? ''));
  const [search, setSearchState] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);

  const [columns, setColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<(string | number)[][]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [related, setRelated] = useState<UseDataBrowserReturn['related']>(null);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState<string | null>(null);

  const clearRelated = useCallback(() => {
    setRelated(null);
    setRelatedError(null);
  }, []);

  // 행 클릭: 선택 갱신 + 드로어 열기 + 이전 연관 행 초기화.
  const openDetail = useCallback((i: number) => {
    setSelectedIndex(i);
    setDetailOpen(true);
    setRelated(null);
    setRelatedError(null);
  }, []);

  // 드로어 닫기 + 연관 행 초기화.
  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setRelated(null);
    setRelatedError(null);
  }, []);

  // 모듈 변경: 테이블을 첫 테이블로 리셋, 검색/페이지/선택/연관 초기화
  const setModule = useCallback((m: string) => {
    setModuleState(m);
    setTableState(firstTable(m));
    setSearchState('');
    setDebouncedSearch('');
    setPage(0);
    setSelectedIndex(0);
    setDetailOpen(false);
    setRelated(null);
    setRelatedError(null);
  }, []);

  // 테이블 변경: 페이지/선택/연관 초기화
  const setTable = useCallback((t: string) => {
    setTableState(t);
    setPage(0);
    setSelectedIndex(0);
    setDetailOpen(false);
    setRelated(null);
    setRelatedError(null);
  }, []);

  // 검색 입력: 원시값 즉시 반영(입력창 제어), 300ms 디바운스로 조회값 갱신 + 페이지 리셋
  const setSearch = useCallback((s: string) => setSearchState(s), []);
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  // rows 페칭
  const loadRows = useCallback(async () => {
    if (!token || !module || !table) return;
    setLoading(true);
    setError(null);
    try {
      const res = await endpoints.fetchTableRows(
        module,
        table,
        { search: debouncedSearch },
        LIMIT,
        page * LIMIT
      );
      setColumns(res.columns);
      setRows(res.rows);
      setTotal(res.total);
      setSelectedIndex(0);
      setDetailOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rows');
      setColumns([]);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token, module, table, page, debouncedSearch]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const tableOptions = getTables(module).map((t) => ({ label: t.label, value: t.name }));
  const fk = getTableDef(module, table)?.fk;

  // 선택 행 + 현재 fk로 연관 조회. rowId = 선택 행에서 현재 테이블 pk 컬럼 값.
  const openRelated = useCallback(async () => {
    if (loading) return;
    const def = getTableDef(module, table);
    if (!def?.fk) return;
    const pkIndex = columns.indexOf(def.pk);
    const row = rows[selectedIndex];
    if (pkIndex < 0 || !row) return;
    const rowId = String(row[pkIndex]);

    setRelatedLoading(true);
    setRelatedError(null);
    try {
      const res = await endpoints.fetchRelatedRows(module, table, rowId, def.fk.table);
      setRelated({
        title: `${def.fk.label} ${res.count.toLocaleString()}건`,
        columns: res.columns,
        rows: res.rows,
      });
    } catch (err) {
      setRelatedError(err instanceof Error ? err.message : 'Failed to load related rows');
      setRelated(null);
    } finally {
      setRelatedLoading(false);
    }
  }, [module, table, columns, rows, selectedIndex, loading]);

  return {
    module,
    moduleOptions: DATA_MODULES,
    setModule,
    table,
    tableOptions,
    setTable,
    search,
    setSearch,
    page,
    setPage,
    columns,
    rows,
    total,
    loading,
    error,
    retry: loadRows,
    selectedIndex,
    setSelectedIndex,
    detailOpen,
    openDetail,
    closeDetail,
    fk,
    related,
    relatedLoading,
    relatedError,
    openRelated,
    clearRelated,
  };
}
